// src/utils/onboardingStorage.js
// Persist onboarding state when user is sent to checkout so we can resume after payment or skip.

const KEY = 'logami_onboarding_pending';

/**
 * @param {Object} state - { responses, selectedPlanId }
 */
export function savePendingOnboarding(state) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('onboardingStorage.savePendingOnboarding', e);
  }
}

/**
 * @returns {{ responses: Object, selectedPlanId: string } | null }
 */
export function loadPendingOnboarding() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.warn('onboardingStorage.loadPendingOnboarding', e);
    return null;
  }
}

export function clearPendingOnboarding() {
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) {
    console.warn('onboardingStorage.clearPendingOnboarding', e);
  }
}

// ─── Landing page exam pre-selection ───────────────────────────────────────
// Persisted in localStorage so it survives the auth redirect.
const LANDING_EXAM_KEY = 'logami_landing_exam_preselect';

/**
 * Save the exam the user selected on the landing page before signing up.
 * @param {{ provider: string, level: string }} exam
 */
export function saveLandingExamPreselect(exam) {
  try {
    localStorage.setItem(LANDING_EXAM_KEY, JSON.stringify(exam));
  } catch (e) {
    console.warn('onboardingStorage.saveLandingExamPreselect', e);
  }
}

/**
 * @returns {{ provider: string, level: string } | null}
 */
export function loadLandingExamPreselect() {
  try {
    const raw = localStorage.getItem(LANDING_EXAM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.provider && parsed?.level ? parsed : null;
  } catch (e) {
    console.warn('onboardingStorage.loadLandingExamPreselect', e);
    return null;
  }
}

export function clearLandingExamPreselect() {
  try {
    localStorage.removeItem(LANDING_EXAM_KEY);
  } catch (e) {
    console.warn('onboardingStorage.clearLandingExamPreselect', e);
  }
}

// ─── Guest exam in progress (started on /try, interrupted by the signup gate) ─
// Written when the gate sends a guest to sign up; read by OnboardingPage (exam
// prefill) and CompactDashboard ("Continue your exam" banner); cleared when the
// exam is finished, and on logout. TTL matches the simulator's on-device
// session snapshot, so the banner never points at an expired session.
//
// The marker is written while the visitor is still anonymous, so it has no
// owner until the first signed-in read CLAIMS it (stamping that uid). After
// that only that account can see it: on a shared device, signing out and into a
// second account must not inherit the first account's unfinished exam.
const GUEST_EXAM_KEY = 'logami:v1:guestExamPending';
const GUEST_EXAM_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * @param {{ provider: string, level: string }} exam
 */
export function saveGuestExamPending(exam) {
  try {
    localStorage.setItem(GUEST_EXAM_KEY, JSON.stringify({ ...exam, savedAt: Date.now() }));
  } catch (e) {
    console.warn('onboardingStorage.saveGuestExamPending', e);
  }
}

/**
 * @param {string|null} [uid] the signed-in uid reading the marker, or null for
 *   an anonymous visitor. A signed-in read claims an unowned marker; a marker
 *   owned by another uid is invisible.
 * @returns {{ provider: string, level: string, savedAt: number, uid?: string } | null}
 */
export function loadGuestExamPending(uid = null) {
  try {
    const raw = localStorage.getItem(GUEST_EXAM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.provider || !parsed?.level) return null;
    if (parsed.savedAt && Date.now() - parsed.savedAt > GUEST_EXAM_TTL_MS) {
      localStorage.removeItem(GUEST_EXAM_KEY);
      return null;
    }
    // Owned by somebody else, or owned at all while nobody is signed in.
    if (parsed.uid && parsed.uid !== uid) return null;
    // First signed-in read claims it.
    if (!parsed.uid && uid) {
      parsed.uid = uid;
      localStorage.setItem(GUEST_EXAM_KEY, JSON.stringify(parsed));
    }
    return parsed;
  } catch (e) {
    console.warn('onboardingStorage.loadGuestExamPending', e);
    return null;
  }
}

export function clearGuestExamPending() {
  try {
    localStorage.removeItem(GUEST_EXAM_KEY);
  } catch (e) {
    console.warn('onboardingStorage.clearGuestExamPending', e);
  }
}
